import React from 'react';
import { MapPin, Database, AlertCircle, CheckCircle, Building2 } from "lucide-react";
import { SiteOffice } from '@/services/siteOfficeService';

interface SiteOfficeCardProps {
  office: SiteOffice;
  isSelected: boolean;
  hasData: boolean;
  identifier: string;
  onSelect: (officeName: string) => void;
}

const SiteOfficeCard = ({ office, isSelected, hasData, identifier, onSelect }: SiteOfficeCardProps) => {
  return (
    <div
      className={`group relative p-5 rounded-2xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
        isSelected
          ? 'bg-gradient-to-br from-[#062a4a] to-[#0c4a6e] shadow-xl shadow-sky-900/25'
          : 'bg-white border-2 border-gray-100 hover:border-sky-300 hover:shadow-lg'
      }`}
      onClick={() => onSelect(office.name)}
    >
      {/* Selection Check */}
      <div className="absolute top-4 right-4">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 ${
          isSelected
            ? 'bg-white text-[#0284c7]'
            : 'border-2 border-gray-300 group-hover:border-sky-400'
        }`}>
          {isSelected && <CheckCircle className="w-5 h-5" />}
        </div>
      </div>

      {/* Icon and Name */}
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isSelected
            ? 'bg-white/15'
            : 'bg-gradient-to-br from-sky-50 to-sky-100'
        }`}>
          <Building2 className={`w-7 h-7 ${isSelected ? 'text-[#38bdf8]' : 'text-[#0284c7]'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-lg leading-tight transition-colors ${
            isSelected ? 'text-white' : 'text-gray-800 group-hover:text-[#0284c7]'
          }`}
            style={{ fontFamily: "'Sora', system-ui, sans-serif" }}
          >
            {office.name.replace('Site Office Regional ', '')}
          </h3>
          {office.location && (
            <div className={`flex items-center gap-1 mt-1 ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
              <MapPin className="w-3.5 h-3.5" />
              <span className="text-sm truncate">{office.location}</span>
            </div>
          )}
        </div>
      </div>

      {/* Data Status Badge */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
        isSelected
          ? hasData ? 'bg-white/20 text-white' : 'bg-white/10 text-white/80'
          : hasData ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
      }`}>
        {hasData ? (
          <>
            <Database className="w-4 h-4" />
            <span>Data Tersedia</span>
          </>
        ) : (
          <>
            <AlertCircle className="w-4 h-4" />
            <span>Belum Ada Data</span>
          </>
        )}
      </div>

      {/* Office ID */}
      <div className={`mt-3 pt-3 border-t flex items-center justify-between ${
        isSelected ? 'border-white/20' : 'border-gray-100'
      }`}>
        <span className={`text-xs ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>ID:</span>
        <span className={`text-xs font-mono font-semibold px-2 py-0.5 rounded ${
          isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
        }`}>
          {identifier}
        </span>
      </div>
    </div>
  );
};

export default SiteOfficeCard;
